import { UUID } from 'crypto';

type ISODateString = string;
type MarkdownEnabledContent = string;

type IChatRole = 'user' | 'assistant' | 'system';

interface IChatMessageBase {
  id: UUID;
  createdAt: ISODateString;
  role: IChatRole;
  content: MarkdownEnabledContent;
}

interface IChatMessageText extends IChatMessageBase {
  type: 'text';
}

interface IChatMessageUserResponseFlow extends IChatMessageBase {
  type: 'flow';

  role: 'user';
  flowId_selectionIndex: [UUID, number];
}

interface IChatMessageAssistantResponseFlow extends IChatMessageBase {
  type: 'flow';

  role: 'assistant';
  flowId: UUID;
}

export type IChatMessage = IChatMessageText | IChatMessageUserResponseFlow | IChatMessageAssistantResponseFlow;

// MARK: - Predefined Flow Graph

interface IFlowGraphNode {
  id: UUID;
  content: MarkdownEnabledContent;
  intent?: 'confirm' | 'deny'

  options?: {
    text: string;
    nextNodeId: UUID;
    customEvent?: string;
  }[]

  shouldAllowUserInput?: boolean;
}

export const FLOW_IDS: Record<string, UUID> = {
  INITIAL_FLOW_ID: 'e56eae43-35b4-4d2f-ac52-279d8a940917',
  BEST_MATCH_REPLY_ID: '6ceadaaf-9f97-41e4-84d7-44ba0791a7ed',
  ASK_WHICH_TOOL_REPLY_ID: '3601af20-f895-4f4b-8535-cabd3777d0b5',
  ASK_WHICH_TOOLS_COMPARE_REPLY_ID: '5c1f80c2-8659-47d1-8bfe-2a5e06acfbef',
  COMPARE_TOOLS_LINK_REPLY_ID: '2e2ba8bc-510d-4db4-9ed5-5970617d7b93',
  THUMBS_UP_FLOW_ID: '78a521df-8686-4ced-8ae6-8f27c21366d7',
  THUMBS_DOWN_FLOW_ID: 'a71d9f83-2284-41a6-aa98-fdcd7377f924',
  THUMBS_DOWN_SHARE_CONFIRMED_ID: 'e9641fdc-a671-4892-894a-10f9caf699cf',
  THUMBS_DOWN_SHARE_DECLINED_ID: 'b103e483-c637-47af-890c-b33c985f0dd2',
}

export const FLOW_GRAPH: Record<UUID, IFlowGraphNode> = {
  [FLOW_IDS.INITIAL_FLOW_ID]: {
    id: FLOW_IDS.INITIAL_FLOW_ID,
    content: `
Hello! I'm MSP's AI Assistant.
How can I help you today?
    `,
    options: [
      {
        text: "Help me find the right tool for my needs",
        nextNodeId: FLOW_IDS.BEST_MATCH_REPLY_ID,
        customEvent: 'suggested_chat_best_match_selected'
      },
      {
        text: "Tell me more about a specific tool",
        nextNodeId: FLOW_IDS.ASK_WHICH_TOOL_REPLY_ID,
        customEvent: 'suggested_chat_ask_which_tool_selected'
      },
      {
        text: "Compare tools side by side",
        nextNodeId: FLOW_IDS.ASK_WHICH_TOOLS_COMPARE_REPLY_ID,
        customEvent: 'suggested_chat_compare_tools_selected'
      }
    ],
    shouldAllowUserInput: true
  },
  [FLOW_IDS.BEST_MATCH_REPLY_ID]: {
    id: FLOW_IDS.BEST_MATCH_REPLY_ID,
    content: `I recommend trying the Best Match tool. It helps find you the best option for your needs. You can [start here](/pages/selection-wizard), and I will assist you along the way.`,
  },
  [FLOW_IDS.ASK_WHICH_TOOL_REPLY_ID]: {
    id: FLOW_IDS.ASK_WHICH_TOOL_REPLY_ID,
    content: `Certainly! What tool would you like to learn more about?`,
  },
  [FLOW_IDS.ASK_WHICH_TOOLS_COMPARE_REPLY_ID]: {
    id: FLOW_IDS.ASK_WHICH_TOOLS_COMPARE_REPLY_ID,
    content: `Certainly. Which tools would you like to compare?`,
  },
  [FLOW_IDS.COMPARE_TOOLS_LINK_REPLY_ID]: {
    id: FLOW_IDS.COMPARE_TOOLS_LINK_REPLY_ID,
    content: `Got it. You can compare the tools at the following link: [Compare Tools](/pages/model-comparator). Let me know if you'd like any help interpreting the results.`,
  },
  [FLOW_IDS.THUMBS_UP_FLOW_ID]: {
    id: FLOW_IDS.THUMBS_UP_FLOW_ID,
    content: `Thank you for your feedback.`,
  },
  [FLOW_IDS.THUMBS_DOWN_FLOW_ID]: {
    id: FLOW_IDS.THUMBS_DOWN_FLOW_ID,
    content: `Thank you for your feedback. May we share this conversation with the MSP team to help improve the AI Assistant?

Please note, this request will only appear once during this chat session.`,
    options: [
      {
        text: "Yes, you can share it",
        nextNodeId: FLOW_IDS.THUMBS_DOWN_SHARE_CONFIRMED_ID
      },
      {
        text: "No, don't share",
        nextNodeId: FLOW_IDS.THUMBS_DOWN_SHARE_DECLINED_ID
      }
    ]
  },
  [FLOW_IDS.THUMBS_DOWN_SHARE_CONFIRMED_ID]: {
    id: FLOW_IDS.THUMBS_DOWN_SHARE_CONFIRMED_ID,
    content: `Thank you. Your input will help us improve the AI Assistant.`,
    intent: 'confirm'
  },
  [FLOW_IDS.THUMBS_DOWN_SHARE_DECLINED_ID]: {
    id: FLOW_IDS.THUMBS_DOWN_SHARE_DECLINED_ID,
    content: `No Problem. This conversation will not be shared.`,
    intent: 'deny'
  }
};
